import React from 'react';
import {
    BsCircleFill,
    BsExclamationTriangleFill,
    BsClockFill,
    BsCreditCardFill,
    BsCalendarWeekFill
} from 'react-icons/bs';

// Filter shape constant
export const DEFAULT_FILTERS = {
    department: '',
    site: '',
    role: '',
    reportedToFinance: '',
    period: '',
    weekending: '',
    cat: '',
    future: false,
};

// Column config arrays
export const COSTING_COLUMNS = [
    { key: 'invoiced', label: 'Received' },
    { key: 'reportedToFinance', label: 'Reported' },
    { key: 'jobdate', label: 'Date' },
    { key: 'site', label: 'Site' },
    { key: 'provider', label: 'Staff' },
    { key: 'role', label: 'Role' },
    { key: 'gigpig', label: 'Approve/Challenge' },
    { key: 'hours', label: 'Hours' },
    { key: 'nettCost', label: 'Net Cost' },
    { key: 'start', label: 'Start' },
    { key: 'finish', label: 'Finish' },
    { key: 'managerSig', label: 'Manager Signature' },
    { key: 'notes', label: 'Notes' },
    { key: 'rating', label: 'Rating' },
    { key: 'rw', label: 'LineID' },
];

export const QUERY_COLUMNS = [
    { key: 'id', label: 'QueryID' },
    { key: 'lineID', label: 'LineID' },
    { key: 'notes', label: 'Reference' },
    { key: 'dateLogged', label: 'Date Logged' },
    { key: 'querytype', label: 'Query Type' },
    { key: 'sitename', label: 'Site Name' },
    { key: 'raisedby', label: 'Raised by' },
    { key: 'roledepartment', label: 'Role Department' },
    { key: 'dateOfJob', label: 'Date Of Job' },
    { key: 'hrsBilled', label: 'Hours' },
    { key: 'rateBilled', label: 'Rate' },
    { key: 'providerName', label: 'Provider Name' },
    { key: 'queryContent', label: 'Query Content' },
    { key: 'billednett', label: 'Billed Nett' },
    { key: 'queryCorrectNett', label: 'Correct Nett' },
    { key: 'queryCorrectHrs', label: 'Correct Hours' },
    { key: 'dateResolved', label: 'Date Resolved' },
    { key: 'resolvedNett', label: 'Resolved Nett' },
    { key: 'credited', label: 'Credited' },
];

// Storage keys
export const TOUR_STORAGE_KEY = 'managerReport_tourSeen';
export const ENTERTAINMENT_STORAGE_KEY = 'managerReport_entertainmentSeen';

// Layout constants
export const ROW_HEIGHT = 44;
export const TABLE_WIDTH = 1700;
export const CARD_DEFAULT_HEIGHT = 350;

// Shared formatters
export function formatTime(timeString) {
    if (timeString === null) return "null";
    if (!timeString) return "";
    return timeString.slice(0, 5);
}

// Colour takes priority: yellow = Late, green = Credited (only if a credit date is present).
// If the row is green but `credited` is null, fall through so the SP discrepancy is visible.
export const resolveCategory = (cat, rowHighlightColour, credited) => {
    const c = (cat || "").toLowerCase();
    if (c === "future booking") return "Future Booking";
    const colour = (rowHighlightColour || "").toUpperCase();
    if (colour === "#FFFF00") return "Late";
    if (colour === "#A4F5A9" && credited != null) return "Credited";
    if (c === "new" || c === "under query") return "New";
    if (c === "missing") return "Missing";
    return "";
};

export const getIconBasedOnCat = (cat) => {
    switch (cat) {
        case "New":
            return <BsCircleFill color="blue" />;
        case "Credited":
            return <BsCreditCardFill color="green" />;
        case "Late":
            return <BsClockFill color="orange" />;
        case "Missing":
            return <BsExclamationTriangleFill color="red" />;
        case "Future Booking":
            return <BsCalendarWeekFill color="#64748b" />;
        default:
            return "";
    }
};

export const getTextBasedOnCat = (cat) => {
    switch (cat) {
        case "New":
            return "New Charges";
        case "Credited":
            return "Credited";
        case "Late":
            return "Received Late";
        case "Missing":
            return "Missing";
        case "Future Booking":
            return "Future Bookings";
        default:
            return "";
    }
};

export const buildLink = (site) => {
    return `${site.baseurl}e_value=${site.email}&opus_bun=${site.bun}&t_value=${site.token}`;
};

export const rowHighlightStyle = (tableData, index) => {
    if (tableData.length > 0 && index >= 0) {
        const rowData = tableData[index];
        if (rowData.rowHighlightColour) {
            return { backgroundColor: rowData.rowHighlightColour, color: '#0c022f' };
        } else if (rowData.credited) {
            return { backgroundColor: 'rgba(255,255,255,0.06)', textDecoration: 'line-through', opacity: 0.6 };
        }
    }
};

export const stripHtml = (html) => {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
};

export const escapeCSV = (val) => {
    if (val === null || val === undefined) return '';
    const str = String(val);
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
};

export const buildExportColumns = (formatShortDate) => [
    { key: 'invoiced', header: 'Received', format: (v) => formatShortDate(v) },
    { key: 'reportedToFinance', header: 'Reported', format: (v) => formatShortDate(v) },
    { key: 'jobdate', header: 'Date', format: (v) => formatShortDate(v) },
    { key: 'site', header: 'Site' },
    { key: 'bun', header: 'Staff' },
    { key: 'role', header: 'Role' },
    { key: 'hours', header: 'Hours' },
    { key: 'nettCost', header: 'Net Cost' },
    { key: 'start', header: 'Start', format: (v) => formatTime(v) },
    { key: 'finish', header: 'Finish', format: (v) => formatTime(v) },
    { key: 'managerSignature', header: 'Manager Signature' },
    { key: 'notes', header: 'Notes' },
    { key: 'rating', header: 'Rating' },
    { key: 'lineId', header: 'LineID' },
    { key: 'infoPanel', header: 'Info', format: (v) => stripHtml(v) },
];

// Dark-theme select styles shared across sidebar filters
export const sidebarSelectStyles = {
    control: (base) => ({
        ...base,
        minHeight: '38px',
        fontSize: '13px',
        borderColor: 'rgba(255,255,255,0.12)',
        backgroundColor: 'rgba(255,255,255,0.03)',
        borderRadius: '6px',
        boxShadow: 'none',
        '&:hover': { borderColor: 'rgba(255,255,255,0.25)' }
    }),
    singleValue: (base) => ({ ...base, color: 'white' }),
    placeholder: (base) => ({ ...base, color: 'rgba(255,255,255,0.5)' }),
    input: (base) => ({ ...base, color: 'white' }),
    menu: (base) => ({ ...base, zIndex: 9999, backgroundColor: '#1e163c', border: '1px solid rgba(255,255,255,0.12)' }),
    option: (base, state) => ({
        ...base,
        fontSize: '13px',
        backgroundColor: state.isSelected ? '#CB2C84' : state.isFocused ? 'rgba(0,93,229,0.1)' : 'transparent',
        color: 'white',
        '&:active': { backgroundColor: 'rgba(0,93,229,0.2)' }
    }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base) => ({ ...base, color: 'rgba(255,255,255,0.5)', '&:hover': { color: 'white' } }),
    clearIndicator: (base) => ({ ...base, color: 'rgba(255,255,255,0.5)', '&:hover': { color: 'white' } }),
};

export const customSelectStyles = {
    control: (base) => ({
        ...base,
        minHeight: '42px',
        fontSize: '14px',
        borderColor: 'rgba(255,255,255,0.12)',
        backgroundColor: 'rgba(255,255,255,0.03)',
        borderRadius: '6px',
        boxShadow: 'none',
        '&:hover': {
            borderColor: 'rgba(255,255,255,0.25)'
        }
    }),
    option: (base, state) => ({
        ...base,
        fontSize: '14px',
        backgroundColor: state.isSelected ? '#CB2C84' : state.isFocused ? 'rgba(0,93,229,0.3)' : '#1a1a2e',
        color: 'white'
    }),
    menu: (base) => ({
        ...base,
        zIndex: 9999,
        borderRadius: '6px',
        backgroundColor: '#1a1a2e'
    }),
    singleValue: (base) => ({
        ...base,
        fontSize: '14px',
        color: 'white'
    }),
    placeholder: (base) => ({
        ...base,
        fontSize: '14px',
        color: 'rgba(255,255,255,0.78)'
    }),
    dropdownIndicator: (base) => ({
        ...base,
        color: 'rgba(255,255,255,0.5)',
        '&:hover': { color: 'white' }
    }),
    clearIndicator: (base) => ({
        ...base,
        color: 'rgba(255,255,255,0.5)',
        '&:hover': { color: 'white' }
    }),
    indicatorSeparator: (base) => ({
        ...base,
        backgroundColor: 'rgba(255,255,255,0.12)'
    }),
    input: (base) => ({
        ...base,
        color: 'white'
    })
};
